import React, { useRef, useMemo, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useMotionTemplate, MotionValue, useSpring } from 'framer-motion';
import { Rocket, Globe, Cpu, Sparkles, Orbit, Layers } from 'lucide-react';
import { clsx } from 'clsx';

// --- Types & Interfaces ---

interface CardProps {
  index: number;
  icon: React.ReactNode;
  title: string;
  content: string;
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
}

// --- Helper Components ---

// Sparkling star effect that activates on hover
const HoverSparkle: React.FC<{ delay: number; x: number; y: number; size: number }> = ({ delay, x, y, size }) => (
  <motion.div
    variants={{
      hover: {
        opacity: [0, 1, 0],
        scale: [0, 1.2, 0],
        y: [0, -40], // Float upward
        rotate: [0, 45],
        transition: {
          duration: 1.5,
          repeat: Infinity,
          delay: delay,
          ease: "easeInOut"
        }
      },
      rest: {
        opacity: 0,
        scale: 0,
        y: 0,
        rotate: 0,
        transition: { duration: 0.3 }
      }
    }}
    className="absolute pointer-events-none text-orange-200/80 drop-shadow-[0_0_8px_rgba(253,186,116,0.6)]"
    style={{
      left: `${x}%`,
      top: `${y}%`,
      width: size,
      height: size,
      zIndex: 20
    }}
  >
    {/* Four-point star shape */}
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
      <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
    </svg>
  </motion.div>
);

const OrbitalCard: React.FC<CardProps> = ({ index, icon, title, content, mouseX, mouseY }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Magnetic Tilt Logic - Input
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Smooth Physics (Springs) - Creates the "floating" feel
  const springX = useSpring(x, { stiffness: 100, damping: 15, mass: 0.1 });
  const springY = useSpring(y, { stiffness: 100, damping: 15, mass: 0.1 });

  // Map smooth values to rotation degrees (Numbers)
  const rotateXDegrees = useTransform(springY, [-0.5, 0.5], [15, -15]);
  const rotateYDegrees = useTransform(springX, [-0.5, 0.5], [-15, 15]);

  // Create CSS transform strings
  const rotateX = useMotionTemplate`${rotateXDegrees}deg`;
  const rotateY = useMotionTemplate`${rotateYDegrees}deg`;

  // Internal highlight based on tilt angle
  const diagonalMovement = useTransform(
    [rotateXDegrees, rotateYDegrees],
    (values: any[]) => {
      const [rX, rY] = values;
      // rX and rY are numbers from the transform
      return `linear-gradient(${135 + rX + rY}deg, rgba(255,255,255,0.15), rgba(255,255,255,0))`;
    }
  );

  // Generate deterministic random positions for sparkles
  const sparkles = useMemo(() => {
    return [...Array(6)].map((_, i) => ({
      id: i,
      x: Math.random() * 80 + 10, // Avoid very edges
      y: Math.random() * 80 + 10,
      size: Math.random() * 12 + 8, // 8px to 20px
      delay: Math.random() * 1
    }));
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXRel = e.clientX - rect.left;
    const mouseYRel = e.clientY - rect.top;

    // Set raw target values (-0.5 to 0.5)
    x.set((mouseXRel / width) - 0.5);
    y.set((mouseYRel / height) - 0.5);
  };

  const handleMouseLeave = () => {
    // Reset to center
    x.set(0);
    y.set(0);
  };

  return (
    <div className="relative group perspective-1000 w-full h-full min-h-[240px]">
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        whileHover="hover"
        initial="rest"
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        className="w-full h-full"
      >
        {/* Morphing Blob Background (Behind Card) */}
        <div className="absolute inset-0 -z-10 translate-y-4 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="w-full h-full bg-gradient-to-r from-orange-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse" />
        </div>

        {/* Card Container (Glass) */}
        <div
          style={{ transform: "translateZ(30px)" }}
          className={clsx(
            "relative h-full w-full rounded-2xl p-6 md:p-8 flex flex-col justify-between overflow-hidden",
            "bg-slate-900/40 backdrop-blur-xl border border-white/10",
            "shadow-[0_8px_32px_0_rgba(0,0,0,0.36)]",
            "group-hover:border-white/20 transition-colors duration-300"
          )}
        >
          {/* Iridescent Border Shine */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-shimmer" />
          </div>

          {/* Dynamic Highlight Overlay */}
          <motion.div
            style={{ background: diagonalMovement }}
            className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />

          {/* Content Layer */}
          <div className="relative z-10 transform-style-3d">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 shadow-inner group-hover:scale-110 transition-transform duration-300 relative z-20">
                <div className="text-orange-200">{icon}</div>
              </div>
              <span className="font-mono text-3xl font-bold text-white/5 group-hover:text-white/10 transition-colors">
                0{index + 1}
              </span>
            </div>

            {/* Title */}
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2 uppercase tracking-wider group-hover:text-orange-100 transition-colors font-['Outfit'] relative z-20">
              {title}
            </h3>

            <p className="text-sm md:text-base text-slate-400 group-hover:text-slate-200 leading-relaxed transition-colors font-light relative z-20">
              {content}
            </p>

            {/* Micro-interaction: Bottom Line */}
            <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-orange-400 to-transparent w-0 group-hover:w-full transition-all duration-700 ease-out" />
          </div>

          {/* Hover-Triggered Sparkles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            {sparkles.map((s) => (
              <HoverSparkle key={s.id} {...s} />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// --- Main Component ---

const AboutSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track the scroll of the entire tall section.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"]
  });

  // Cursor Tracking for Dynamic Lighting
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { clientX, clientY } = e;
    const { left, top } = e.currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  };

  // --- Animation Sequence Configuration ---

  // 1. Heading Reveal (0 - 15%)
  const headingOpacity = useTransform(scrollYProgress, [0.05, 0.15], [0, 1]);
  const headingScale = useTransform(scrollYProgress, [0.05, 0.15], [0.8, 1]);
  const headingBlur = useTransform(scrollYProgress, [0.05, 0.15], ["blur(10px)", "blur(0px)"]);

  // 2. Subheading Reveal (15% - 25%)
  const subOpacity = useTransform(scrollYProgress, [0.15, 0.25], [0, 1]);
  const subY = useTransform(scrollYProgress, [0.15, 0.25], [30, 0]);
  const subBlur = useTransform(scrollYProgress, [0.15, 0.25], ["blur(10px)", "blur(0px)"]);

  // --- Vertical Pan Logic to prevent cutoff ---
  // Responsive pan distance: Mobile needs much more lift due to single column stacking
  const panEnd = isMobile ? "-65%" : "-25%";
  const contentPanY = useTransform(scrollYProgress, [0.3, 0.95], ["0%", panEnd]);

  // 3. Card 1 (Top Left) (0.25 - 0.40)
  const c1X = useTransform(scrollYProgress, [0.25, 0.40], ["-50vw", "0vw"]);
  const c1Opacity = useTransform(scrollYProgress, [0.25, 0.40], [0, 1]);
  const c1Blur = useTransform(scrollYProgress, [0.25, 0.40], ["blur(20px)", "blur(0px)"]);

  // 4. Card 2 (Top Right) (0.40 - 0.55)
  const c2X = useTransform(scrollYProgress, [0.40, 0.55], ["50vw", "0vw"]);
  const c2Opacity = useTransform(scrollYProgress, [0.40, 0.55], [0, 1]);
  const c2Blur = useTransform(scrollYProgress, [0.40, 0.55], ["blur(20px)", "blur(0px)"]);

  // 5. Card 3 (Bottom Left) (0.55 - 0.70)
  const c3X = useTransform(scrollYProgress, [0.55, 0.70], ["-50vw", "0vw"]);
  const c3Y = useTransform(scrollYProgress, [0.55, 0.70], ["50vh", "0vh"]);
  const c3Opacity = useTransform(scrollYProgress, [0.55, 0.70], [0, 1]);
  const c3Blur = useTransform(scrollYProgress, [0.55, 0.70], ["blur(20px)", "blur(0px)"]);

  // 6. Card 4 (Bottom Right) (0.70 - 0.85)
  const c4X = useTransform(scrollYProgress, [0.70, 0.85], ["50vw", "0vw"]);
  const c4Y = useTransform(scrollYProgress, [0.70, 0.85], ["50vh", "0vh"]);
  const c4Opacity = useTransform(scrollYProgress, [0.70, 0.85], [0, 1]);
  const c4Blur = useTransform(scrollYProgress, [0.70, 0.85], ["blur(20px)", "blur(0px)"]);

  const cardData = [
    {
      icon: <Rocket size={24} />,
      title: "Our Mission",
      content: "Propelling visionary ideas."
    },
    {
      icon: <Globe size={24} />,
      title: "Expertise",
      content: "Global Standards."
    },
    {
      icon: <Sparkles size={24} />,
      title: "Innovation",
      content: "AI-First Solutions."
    },
    {
      icon: <Cpu size={24} />,
      title: "Tech Stack",
      content: "Modern & Robust."
    }
  ];

  return (
    <section
      id="about"
      ref={containerRef}
      className="relative h-[400vh] bg-black"
    >
      {/* Sticky wrapper holds the visual viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col items-center justify-start pt-20 md:pt-36 px-6 perspective-1000">

        {/* --- Background Elements --- */}
        <div
          onMouseMove={handleMouseMove}
          className="absolute inset-0 z-0 pointer-events-none"
        >
          {/* Main Space Background Image - Fixed opacity */}
          <img
            src="https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?q=80&w=2072&auto=format&fit=crop"
            alt="Deep Space Background"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />

          {/* Searchlight effect */}
          <motion.div
            className="absolute -inset-px opacity-30 transition duration-300"
            style={{
              background: useMotionTemplate`
                radial-gradient(
                  650px circle at ${mouseX}px ${mouseY}px,
                  rgba(255, 100, 50, 0.08),
                  transparent 80%
                )
              `,
            }}
          />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />

          <motion.div
            style={{ rotate: useTransform(scrollYProgress, [0, 1], [0, 360]) }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-dashed border-white/5 rounded-full"
          />
        </div>

        {/* --- Pan Wrapper (Moves up to reveal bottom cards) --- */}
        <motion.div
          style={{ y: contentPanY }}
          className="relative z-10 w-full flex flex-col items-center pb-24"
        >
          {/* --- Header Section --- */}
          <div className="text-center mb-8 md:mb-10 max-w-4xl mx-auto w-full">
            <motion.div
              style={{
                opacity: headingOpacity,
                scale: headingScale,
                filter: headingBlur
              }}
              className="relative inline-block"
            >
              <h2 className="text-5xl md:text-8xl font-bold text-white pb-4 uppercase tracking-tighter leading-none font-['Outfit'] drop-shadow-2xl">
                About
              </h2>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-32 h-1 bg-orange-500 rounded-full blur-md opacity-80" />
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-16 h-1 bg-white rounded-full opacity-90" />
            </motion.div>

            <motion.p
              style={{
                opacity: subOpacity,
                y: subY,
                filter: subBlur
              }}
              className="mt-8 text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-light"
            >
              Launching Applications Into Automated Orbit.
            </motion.p>
          </div>

          {/* --- Card Grid --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 w-full max-w-6xl mx-auto content-center">

            {/* 1. Top Left Card */}
            <motion.div style={{ x: c1X, opacity: c1Opacity, filter: c1Blur }}>
              <OrbitalCard index={0} {...cardData[0]} mouseX={mouseX} mouseY={mouseY} />
            </motion.div>

            {/* 2. Top Right Card */}
            <motion.div style={{ x: c2X, opacity: c2Opacity, filter: c2Blur }}>
              <OrbitalCard index={1} {...cardData[1]} mouseX={mouseX} mouseY={mouseY} />
            </motion.div>

            {/* 3. Bottom Left Card */}
            <motion.div style={{ x: c3X, y: c3Y, opacity: c3Opacity, filter: c3Blur }}>
              <OrbitalCard index={2} {...cardData[2]} mouseX={mouseX} mouseY={mouseY} />
            </motion.div>

            {/* 4. Bottom Right Card */}
            <motion.div style={{ x: c4X, y: c4Y, opacity: c4Opacity, filter: c4Blur }}>
              <OrbitalCard index={3} {...cardData[3]} mouseX={mouseX} mouseY={mouseY} />
            </motion.div>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default AboutSection;